import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Clock, Loader2, LocateFixed, MapPin, Navigation } from "lucide-react";
import { api } from "../../lib/api";
import { AGENCIAS } from "../../lib/site";
import { directionsUrl, distanceKm, fmtKm, placeFor } from "../../lib/stopPlaces";

const pin = (n, end) => L.divIcon({
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  html: `<span style="display:grid;place-items:center;width:30px;height:30px;border-radius:999px;background:${end ? "#0B110D" : "#0B5323"};color:${end ? "#22FF00" : "#fff"};border:3px solid #fff;box-shadow:0 6px 16px rgba(0,0,0,.3);font:700 12px/1 monospace">${n}</span>`,
});

const useAgencies = () => {
  const [list, setList] = useState(AGENCIAS);
  useEffect(() => {
    let alive = true;
    api.get("/agencies").then((r) => { if (alive && Array.isArray(r.data) && r.data.length) setList(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return list;
};

const Directions = ({ p, testid }) => (
  <a href={directionsUrl(p)} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border-2 border-[var(--forest)] px-3 py-1.5 text-xs font-bold text-[var(--forest)] transition-colors hover:bg-[var(--forest)] hover:text-white">
    <Navigation className="h-3.5 w-3.5" /> Cómo llegar
  </a>
);

const groupTimes = (times) => Object.values(times.reduce((g, x) => {
  const k = `${x.arrive}|${x.t}`;
  g[k] = g[k] ? { ...g[k], days: `${g[k].days} · ${x.days}` } : { ...x };
  return g;
}, {}));

const Times = ({ s, n, where }) => {
  const list = groupTimes(s.times);
  return (
  <ul data-testid={`${where}-times-${n}`} className="!my-1 space-y-0.5 text-xs">
    {list.map((x, i) => (
      <li key={i} className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 shrink-0 text-[var(--forest)]" />
        <span><b className="font-mono">{x.arrive ? "Llega" : "Sale"} {x.t} hs</b>{list.length > 1 && ` · ${x.days}`}</span>
      </li>
    ))}
  </ul>
  );
};

const useNearest = (stops, onFound) => {
  const [me, setMe] = useState(null);
  const [state, setState] = useState({ locating: false, error: "" });
  const near = useMemo(() => {
    if (!me) return null;
    return stops.map((s, n) => ({ n, km: distanceKm(me, s.place) })).sort((a, b) => a.km - b.km)[0] || null;
  }, [me, stops]);
  const locate = () => {
    if (!navigator.geolocation) { setState({ locating: false, error: "Tu dispositivo no permite compartir la ubicación." }); return; }
    setState({ locating: true, error: "" });
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const pos = { lat: coords.latitude, lng: coords.longitude };
        setMe(pos);
        setState({ locating: false, error: "" });
        const best = stops.map((s, n) => ({ n, km: distanceKm(pos, s.place) })).sort((a, b) => a.km - b.km)[0];
        if (best) onFound(pos, best.n);
      },
      () => setState({ locating: false, error: "No pudimos obtener tu ubicación. Revisá los permisos del navegador." }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  return { me, near, locate, ...state };
};

const NearestBar = ({ g, stops }) => (
  <div className="mt-5 flex flex-wrap items-center gap-3">
    <button type="button" onClick={g.locate} disabled={g.locating} data-testid="stops-nearest-button"
      className="inline-flex items-center gap-2 rounded-full bg-[var(--lime)] px-5 py-2.5 text-sm font-bold text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-70">
      {g.locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} {g.me ? "Actualizar mi ubicación" : "Parada más cercana a mí"}
    </button>
    {g.near && (
      <p data-testid="stops-nearest-result" className="text-sm">
        Tu parada más cercana: <b>{stops[g.near.n].name}</b> · a {fmtKm(g.near.km)}
      </p>
    )}
    {g.error && <p data-testid="stops-nearest-error" className="text-sm text-red-700">{g.error}</p>}
  </div>
);

const StopRow = ({ s, n, km }) => (
  <li data-testid={`stop-item-${n}`} className={`flex items-start justify-between gap-3 rounded-2xl p-4 transition-colors duration-300 ${km != null ? "bg-[var(--forest)] text-white [&_p]:text-white [&_ul_svg]:text-[var(--lime)] [&>div>p>span]:text-[var(--lime)]" : "bg-[var(--paper)]"}`}>
    <div className="min-w-0">
      <p className="font-semibold"><span className="mr-2 font-mono text-xs text-[var(--forest)]">{n + 1}</span>{s.name}</p>
      <p className="mt-1 flex items-start gap-1.5 text-xs text-[var(--muted-ink)]"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />{s.place.address}</p>
      <Times s={s} n={n} where="stop" />
      {km != null && <span data-testid="stop-nearest-badge" className="mt-2 inline-block whitespace-nowrap rounded-full bg-[var(--lime)] px-3 py-1 text-xs font-bold text-[var(--ink)]">La más cercana · a {fmtKm(km)}</span>}
    </div>
    <span className="rounded-full bg-white"><Directions p={s.place} testid={`stop-directions-${n}`} /></span>
  </li>
);

export const StopsMap = ({ services, origin, destination }) => {
  const agencies = useAgencies();
  const stops = useMemo(() => {
    const seen = new Map();
    (services || []).forEach((sv) => (sv.stops || []).forEach((x, k) => {
      const time = { t: x.time, days: sv.days, arrive: k === sv.stops.length - 1 };
      if (seen.has(x.city)) { seen.get(x.city).times.push(time); return; }
      const place = placeFor(x.city, agencies);
      if (place) seen.set(x.city, { ...x, place, times: [time] });
    }));
    return [...seen.values()];
  }, [services, agencies]);
  const mapRef = useRef(null);
  const markers = useRef({});
  const g = useNearest(stops, (pos, n) => {
    const p = stops[n].place;
    mapRef.current?.flyToBounds(L.latLngBounds([[pos.lat, pos.lng], [p.lat, p.lng]]), { padding: [50, 50], duration: 1.2, maxZoom: 14 });
    setTimeout(() => markers.current[n]?.openPopup(), 1300);
  });
  if (!stops.length) return null;
  const bounds = L.latLngBounds(stops.map((s) => [s.place.lat, s.place.lng])).pad(0.15);
  const isEnd = (s) => s.city === origin || s.city === destination;
  return (
    <div data-testid="stops-map-section" className="rounded-3xl bg-white p-4 shadow-sm md:p-8">
      <h2 className="font-display text-base md:text-lg">Mapa de paradas de {origin} a {destination}</h2>
      <p className="mt-1 text-sm text-[var(--muted-ink)]">Tocá una parada para ver la dirección, el horario y cómo llegar. Horarios estimados.</p>
      <NearestBar g={g} stops={stops} />
      <div data-testid="stops-map" className="relative z-0 mt-4 h-[340px] overflow-hidden rounded-2xl md:h-[460px]">
        <MapContainer ref={mapRef} bounds={bounds} scrollWheelZoom={false} dragging={!L.Browser.mobile} className="h-full w-full">
          <TileLayer attribution="&copy; OpenStreetMap" url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="map-tiles" />
          <Polyline positions={stops.map((s) => [s.place.lat, s.place.lng])} pathOptions={{ color: "#0B5323", weight: 3, dashArray: "6 8" }} />
          {stops.map((s, n) => (
            <Marker key={s.city} ref={(m) => { markers.current[n] = m; }} position={[s.place.lat, s.place.lng]} icon={pin(n + 1, isEnd(s))}>
              <Popup>
                <p className="!m-0 font-semibold">{s.name}</p>
                <p className="!my-1 text-xs">{s.place.address}</p>
                <Times s={s} n={n} where="map" />
                <Directions p={s.place} testid={`map-directions-${n}`} />
              </Popup>
            </Marker>
          ))}
          {g.me && <CircleMarker center={[g.me.lat, g.me.lng]} radius={9} pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#2563EB", fillOpacity: 1 }}><Popup>Estás acá</Popup></CircleMarker>}
        </MapContainer>
      </div>
      <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{stops.map((s, n) => <StopRow key={s.city} s={s} n={n} km={g.near?.n === n ? g.near.km : null} />)}</ol>
    </div>
  );
};
